import { useTranslation } from "react-i18next";
import { formatPrice, type CheckoutShippingOption } from "@nh/shared";
import { cn } from "@/shared/lib/cn";

export interface ShippingOptionsPickerProps {
  options: readonly CheckoutShippingOption[];
  value: string | null;
  onChange: (serviceCode: string) => void;
  numberLocale: string;
}

/**
 * Los servicios de envío que cotizó Trasso, para elegir uno. Vienen en
 * el orden de Trasso: el primero es su recomendación (precio, tiempo y
 * confiabilidad), y es el que queda elegido de entrada.
 */
export function ShippingOptionsPicker({
  options,
  value,
  onChange,
  numberLocale,
}: ShippingOptionsPickerProps) {
  const { t } = useTranslation();
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-[13px] font-medium text-ds-ink-3">
        {t("¿Cómo quieres recibirlo?")}
      </legend>
      {options.map((option, index) => {
        const selected = option.serviceCode === value;
        return (
          <label
            key={option.serviceCode}
            className={cn(
              "flex cursor-pointer items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-[14px] transition-colors",
              selected
                ? "border-ds-violet-pop bg-ds-violet-pop/5"
                : "border-ds-border bg-white hover:border-ds-ink-3",
            )}
          >
            <span className="flex items-center gap-3">
              <input
                type="radio"
                name="shipping-service"
                value={option.serviceCode}
                checked={selected}
                onChange={() => onChange(option.serviceCode)}
                className="h-4 w-4 accent-ds-violet-pop"
              />
              <span className="grid">
                <span className="font-medium text-ds-ink">
                  {option.carrier} · {option.serviceCode}
                  {index === 0 && (
                    <span className="ml-2 text-[12px] font-normal text-ds-ink-3">
                      {t("Recomendado")}
                    </span>
                  )}
                </span>
                {typeof option.etaDays === "number" && (
                  <span className="text-[12px] text-ds-ink-3">
                    {option.etaDays === 1
                      ? t("Llega en 1 día hábil")
                      : t("Llega en {{n}} días hábiles", { n: option.etaDays })}
                  </span>
                )}
              </span>
            </span>
            <span className="shrink-0 font-semibold text-ds-ink">
              {formatPrice(option.priceCents, "MXN", numberLocale)}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
